import { useSnackbar } from '@b7hio/core-lib/src';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Button, IconButton, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import BlockRounded from '@mui/icons-material/BlockRounded';
import {
  revokeAccess,
  useListPlatformProductsForProduct,
} from '../../../../../../api-lib/src/gen/ops-portal-api';
import { showErrors } from '@b7hio/core-lib/src/utils';

interface Props {
  readonly productUid: string;
}

export const AssignedPlatformProducts = ({ productUid }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['products', 'common']);

  const { data, refetch } = useListPlatformProductsForProduct(productUid);
  const platformProducts = data ?? [];

  const revokePlatformProduct = async (
    platformUid: string,
    platformProductUid: string
  ): Promise<void> => {
    try {
      await revokeAccess(platformUid, platformProductUid);
      enqueueSnackbar(
        t('products:platformProducts.messages.revokedAccessSuccess'),
        'success'
      );
      await refetch();
    } catch (error) {
      showErrors(
        error,
        enqueueSnackbar,
        t('products:platformProducts.messages.revokedAccessFail')
      );
    }
  };

  return (
    <>
      <Typography variant="h6" gutterBottom align="center">
        {t('products:platformProducts.messages.assigned')}
        <Button sx={{ marginLeft: '2rem' }} onClick={() => refetch()}>
          Refresh
        </Button>
      </Typography>
      <DataGridPro
        autoHeight
        columns={[
          {
            field: 'platformName',
            headerName: 'Platform',
            flex: 1,
          },
          {
            field: 'platformProductAlias',
            headerName: 'Alias',
            flex: 1,
          },
          {
            field: 'platformProductUid',
            headerName: 'Platform Product UID',
            minWidth: 290,
            maxWidth: 290,
            resizable: false,
            renderCell: ({ row }) => {
              const assetsDomain = process.env.NEXT_PUBLIC_RESOURCE_URL ?? '';
              const platformProductUid: string = row.platformProductUid;
              // TODO: ideally this would be returned by the API
              const url = `${assetsDomain}/summary-boxes/${platformProductUid}/${platformProductUid} Product Summary Box.pdf`;
              return <a href={url}>{platformProductUid}</a>;
            },
          },
          {
            field: 'productGrossRate',
            headerName: 'Gross Rate',
            minWidth: 100,
            maxWidth: 100,
            resizable: false,
            renderCell: ({ row }) => {
              return (row.productGrossRate * 100).toFixed(2).toString() + '%';
            },
          },
          {
            field: 'bondsmithFeeType',
            headerName: 'Fee Type',
            minWidth: 140,
            maxWidth: 140,
            resizable: false,
          },
          {
            field: 'bondsmithFeeAmount',
            headerName: 'Fee',
            minWidth: 75,
            maxWidth: 75,
            resizable: false,
          },
          {
            field: 'platformFeeAmount',
            headerName: 'Platform Fee',
            minWidth: 105,
            maxWidth: 105,
            resizable: false,
          },
          {
            field: 'minimumDepositLimit',
            headerName: 'Min Deposit',
            flex: 0.8,
            valueFormatter: ({ value }) =>
              t('common:currency', {
                value: {
                  currency: 'GBP',
                  amount: value,
                },
              }),
          },
          {
            field: 'maximumDepositLimit',
            headerName: 'Max Deposit',
            flex: 0.8,
            valueFormatter: ({ value }) =>
              t('common:currency', {
                value: {
                  currency: 'GBP',
                  amount: value,
                },
              }),
          },
          {
            field: 'action',
            headerName: t('products:platformProducts.revoke'),
            minWidth: 70,
            maxWidth: 70,
            resizable: false,
            renderCell: ({ row }) => {
              return (
                <IconButton
                  edge="end"
                  color="error"
                  onClick={async () =>
                    await revokePlatformProduct(
                      row.platformUid,
                      row.platformProductUid
                    )
                  }>
                  <BlockRounded />
                </IconButton>
              );
            },
          },
        ]}
        rows={platformProducts}
        getRowId={(row) => row.platformProductUid}
      />
    </>
  );
};
